import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Download, Smartphone, Shield, ArrowRight, QrCode } from "lucide-react";
import {
  fetchLatestAppRelease,
  getDownloadPagePath,
  resolveDownloadUrl,
} from "@/lib/appDownload";

/**
 * Right column on login — QR + direct download (site-hosted APK, no store badges).
 */
export default function LoginDownloadPanel() {
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const downloadPageUrl =
    typeof window !== "undefined" ? `${window.location.origin}${getDownloadPagePath()}` : getDownloadPagePath();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchLatestAppRelease();
        if (!cancelled) setRelease(data);
      } catch {
        if (!cancelled) setRelease(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const directApkUrl = resolveDownloadUrl(release);
  const qrValue = directApkUrl || downloadPageUrl;

  return (
    <div
      className="flex flex-col h-full p-6 lg:p-8 bg-gradient-to-br from-cm-lavender via-white to-cm-surface border-l border-[var(--cm-border)]"
      data-testid="login-download-panel"
    >
      <div className="cm-landing-section-label mb-3">Mobile app</div>
      <h2 className="text-[22px] lg:text-[26px] font-extrabold text-cm-navy tracking-tight leading-tight">
        Download the CoinMBX app
      </h2>
      <p className="mt-3 text-[13px] text-cm-grey leading-relaxed">
        Install directly from our website — no Play Store or App Store required. Scan the QR code
        on your phone or open the download page.
      </p>

      <div className="mt-4 flex flex-col items-center">
        <div className="relative p-1 rounded-[16px] bg-white border-2 border-cm-blue/15 shadow-[0_16px_40px_-20px_rgba(2,45,238,0.35)]">
          <div className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-cm-blue rounded-tl-md" />
          <div className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-cm-blue rounded-tr-md" />
          <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-cm-blue rounded-bl-md" />
          <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-cm-blue rounded-br-md" />
          <div className="bg-white p-3 rounded-[12px]">
            {loading ? (
              <div className="w-[160px] h-[160px] grid place-items-center text-cm-grey text-[13px]">
                Loading…
              </div>
            ) : (
              <QRCodeSVG value={qrValue} size={160} level="M" includeMargin />
            )}
          </div>
        </div>
        <p className="mt-3 text-[12px] text-cm-grey text-center max-w-[260px] flex items-start gap-2 justify-center">
          <QrCode size={16} className="text-cm-blue shrink-0 mt-0.5" />
          <span>
            {directApkUrl
              ? "Scan to download the latest APK, then sign in with the same account."
              : "Scan to open the download page on your phone."}
          </span>
        </p>
      </div>

      <div className="mt-4 space-y-3">
        {release?.version && (
          <div className="flex items-center justify-between text-[12px] px-3 py-2 rounded-[10px] bg-white border border-[var(--cm-border)]">
            <span className="text-cm-grey">Latest build</span>
            <span className="font-bold text-cm-navy">v{release.version}</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Link
            to={getDownloadPagePath()}
            className="inline-flex items-center justify-center gap-2 h-[44px] rounded-[12px] border-2 border-cm-blue/20 text-cm-blue font-semibold text-[13px] hover:bg-cm-lavender/80 transition"
            data-testid="login-download-page"
          >
            <Smartphone size={16} />
            Download page
          </Link>
          {directApkUrl ? (
            <a
              href={directApkUrl}
              className="inline-flex items-center justify-center gap-2 h-[44px] rounded-[12px] bg-cm-blue text-white font-bold text-[13px] hover:bg-cm-blue-deep transition"
              data-testid="login-download-apk"
            >
              <Download size={16} />
              Get APK
            </a>
          ) : (
            <Link
              to={getDownloadPagePath()}
              className="inline-flex items-center justify-center gap-2 h-[44px] rounded-[12px] bg-cm-blue text-white font-bold text-[13px] hover:bg-cm-blue-deep transition"
              data-testid="login-download-apk-fallback"
            >
              <ArrowRight size={16} />
              Get app
            </Link>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-[var(--cm-border)] flex items-start gap-2 text-[11px] text-cm-grey leading-relaxed">
        <Shield size={14} className="text-cm-blue shrink-0 mt-0.5" />
        <span>
          Sideload only from official CoinMBX links. Enable unknown-app installs if Android prompts
          you during setup.
        </span>
      </div>
    </div>
  );
}
